import { useState } from "react"
import { View, Pressable } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"
import { useLocalSearchParams, Link, router } from "expo-router"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"

import { Header, Text, MCIcon, List, ModalWrapper, TextInput, ActivityIndicator } from "@src/components"
import { trpc, formatDateForDisplay, priceFromDBFormat, sharedStyles, type RouterOutputs } from "@src/utils"
import { ar } from "i18n/ar"
import { PriceInput } from "components/price-input"
import { confirmShiftInput } from "@white-labels/input"

type ShiftData = RouterOutputs["shifts"]["getById"]

function ShiftSummary({ shift }: { shift: ShiftData }) {
  const rows = [
    { label: ar.cash, expected: shift.cash, actual: shift.actualCash },
    { label: ar.visa, expected: shift.visa, actual: shift.actualVisa },
    { label: ar.wallet, expected: shift.wallet, actual: shift.actualWallet },
  ]

  return (
    <View className="mb-3 gap-2">
      <View className="flex-row gap-2">
        {rows.map((row) => {
          const variance = row.actual !== null ? row.actual - row.expected : null
          return (
            <View key={row.label} className="flex-1 bg-white rounded-lg border border-gray-200 px-2 py-2 gap-0.5">
              <Text className="font-semibold text-center text-xs">{row.label}</Text>
              <Text className="text-xs text-gray-500 text-center">{row.actual !== null ? priceFromDBFormat(row.actual) : "—"}</Text>
              {variance !== null ? (
                <Text className={`text-xs font-bold text-center ${variance > 0 ? "text-green-600" : variance < 0 ? "text-red-600" : "text-gray-500"}`}>
                  {variance > 0 ? "+" : ""}
                  {priceFromDBFormat(variance)}
                </Text>
              ) : null}
            </View>
          )
        })}
      </View>
      {shift.confirmationNote ? (
        <View className="bg-white rounded-lg border border-gray-200 px-3 py-2">
          <Text className="text-xs text-gray-500">
            {ar.confirmationNote}: {shift.confirmationNote}
          </Text>
        </View>
      ) : null}
      {shift.updatedAt ? (
        <View className="bg-white rounded-lg border border-gray-200 px-3 py-2">
          <Text className="text-xs text-gray-500">
            {ar.confirmed}: {formatDateForDisplay(shift.updatedAt)}
          </Text>
        </View>
      ) : null}
    </View>
  )
}

function ConfirmShiftModal({ shift, onClose }: { shift: ShiftData; onClose: () => void }) {
  const utils = trpc.useUtils()
  const confirmShift = trpc.shifts.confirm.useMutation()

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(confirmShiftInput),
    defaultValues: { id: shift.id, actualCash: undefined as unknown as number, actualVisa: undefined as unknown as number, actualWallet: undefined as unknown as number },
    mode: "all",
  })

  const { isValid } = formState
  const disabled = !isValid || confirmShift.isPending

  const onSubmit = handleSubmit((data) => {
    confirmShift.mutate(data, {
      onSuccess: () => {
        utils.shifts.getById.invalidate({ id: shift.id })
        utils.shifts.list.invalidate()
        onClose()
      },
    })
  })

  return (
    <ModalWrapper onClose={onClose}>
      <View className="gap-4">
        <Text className="font-bold text-lg text-center mb-2">{ar.confirmShift}</Text>
        <View className="flex-row gap-2">
          {(
            [
              { label: ar.cash, name: "actualCash" as const, expected: shift.cash },
              { label: ar.visa, name: "actualVisa" as const, expected: shift.visa },
              { label: ar.wallet, name: "actualWallet" as const, expected: shift.wallet },
            ] as const
          ).map((row) => (
            <View key={row.label} className="flex-1 gap-1">
              <Text className="font-semibold">{row.label}</Text>
              <Text className="text-xs text-gray-500">{priceFromDBFormat(row.expected)}</Text>
              <Controller control={control} name={row.name} render={({ field }) => <PriceInput value={field.value} onValueChange={field.onChange} />} />
            </View>
          ))}
        </View>
        <Controller
          control={control}
          name="confirmationNote"
          render={({ field }) => (
            <TextInput
              className="bg-white border border-gray-300 rounded px-3 py-2 text-right text-sm text-gray-900 focus:border-blue-600"
              placeholder={ar.addConfirmationNote}
              placeholderTextColor="#9CA3AF"
              value={field.value ?? ""}
              onChangeText={field.onChange}
            />
          )}
        />
        {confirmShift.isError ? <Text className="text-red-600 text-sm text-center">{confirmShift.error.message}</Text> : null}
        <Pressable className={`h-10 rounded items-center justify-center active:opacity-90 ${disabled ? "bg-gray-400" : "bg-blue-600"}`} onPress={onSubmit} disabled={disabled}>
          <Text className="text-white font-bold text-base">{confirmShift.isPending ? ar.confirming : ar.confirmShift}</Text>
        </Pressable>
      </View>
    </ModalWrapper>
  )
}

export default function Shift() {
  const params = useLocalSearchParams<{ id: string }>()
  const shiftId = Number.parseInt(params.id, 10)
  const shift = trpc.shifts.getById.useQuery({ id: shiftId })
  const [confirmVisible, setConfirmVisible] = useState(false)

  if (shift.isLoading) {
    return (
      <>
        <Header title={ar.shifts} />
        <ActivityIndicator className="mt-2" />
      </>
    )
  }

  if (!shift.data) {
    return (
      <>
        <Header title={ar.shifts} />
        <Text>NO DATA</Text>
      </>
    )
  }

  const data = shift.data

  return (
    <>
      <Header
        title={`${ar.shifts} #${data.id}`}
        right={
          <View className="flex-row h-full items-center">
            {data.status === "pending" && (
              <Pressable onPress={() => setConfirmVisible(true)} className="px-2 py-1 active:opacity-70 h-full justify-center items-center">
                <MCIcon name="check-circle-outline" size={24} color="#2563EB" />
              </Pressable>
            )}
            <Pressable onPress={() => router.push(`/dashboard/sales?shiftId=${data.id}`)} className="px-2 py-1 active:opacity-70 h-full justify-center items-center">
              <MCIcon name="chart-bar" size={24} color="#374151" />
            </Pressable>
          </View>
        }
      />

      {confirmVisible && <ConfirmShiftModal shift={data} onClose={() => setConfirmVisible(false)} />}

      <List
        onRefresh={shift.refetch}
        contentContainerStyle={sharedStyles.contentContainer}
        ListHeaderComponent={data.status === "confirmed" ? <ShiftSummary shift={data} /> : null}
        keyExtractor={(item) => item.id.toString()}
        data={data.receipts}
        renderItem={({ item }) => {
          return (
            <Link href={`/dashboard/receipt/new/${item.id}`} asChild>
              <Pressable className="flex-1">
                <View className="bg-white p-4 rounded border border-gray-200 mb-1">
                  <View className="flex-row justify-between items-center">
                    <View className="flex-1">
                      <Text className="font-semibold text-gray-900 text-left">
                        {ar.receiptNumber} #{item.number}
                      </Text>
                      <Text className="text-gray-600 text-sm text-left mt-1">{formatDateForDisplay(item.time)}</Text>
                    </View>
                    <View className="items-end">
                      <Text className="font-bold text-blue-600 text-lg">{priceFromDBFormat(item.amount)}</Text>
                      <Text className="text-gray-500 text-xs mt-1 capitalize">{item.paymentMethod}</Text>
                    </View>
                  </View>
                </View>
              </Pressable>
            </Link>
          )
        }}
        ListFooterComponent={<SafeAreaView edges={["bottom"]} />}
      />
    </>
  )
}
